<template>
  <div class="w-100 login-right pr-5 pt-5">
    <div class="d-block login-text text-right">
      <span class="float-left pl-5">
        <a href="javascript:void(0)" @click="$router.push('/login')" class="no-style">&lt; назад</a>
      </span>
    </div>

    <div class="reg-form pt-5">
      <h5 class="reg-title text-center">Восстановление пароля</h5>

      <!-- Step 1: email -->
      <div v-if="step === 1">
        <p class="fp-desc text-center">
          Введите email, указанный при регистрации. Мы отправим код для сброса пароля.
        </p>

        <div v-if="error" class="fp-error-box mt-3">{{ error }}</div>

        <div class="input-container mt-4">
          <label class="reg-label">Email</label>
          <input
            type="email"
            class="reg-input w-100 p-2"
            v-model.trim="email"
            :disabled="loading"
            placeholder="you@example.com"
            autocomplete="email"
            @keyup.enter="sendCode"
          />
        </div>

        <div ref="turnstile" class="d-flex justify-content-center mt-3"></div>
        <button
          class="submit-btn w-100 py-2 mt-4"
          :disabled="loading || !email"
          @click="sendCode"
        >
          <b-spinner v-if="loading" small class="mr-2"></b-spinner>
          <span class="submit-btn-text">{{ loading ? 'Отправка…' : 'Получить код' }}</span>
        </button>
      </div>

      <!-- Step 2: code + new password -->
      <div v-else-if="step === 2">
        <p class="fp-desc text-center">
          Код отправлен на <strong class="fp-email">{{ email }}</strong>.<br>
          Введите его и задайте новый пароль.
        </p>

        <div v-if="error" class="fp-error-box mt-3">{{ error }}</div>

        <div class="input-container mt-4">
          <label class="reg-label">Код из письма (4 цифры)</label>
          <input
            type="text"
            class="reg-input w-100 p-2 text-center fp-code-input"
            v-model.trim="code"
            :disabled="loading"
            placeholder="0000"
            maxlength="4"
            autocomplete="one-time-code"
            inputmode="numeric"
          />
        </div>

        <div class="input-container mt-3">
          <label class="reg-label">Новый пароль</label>
          <div class="password-wrapper">
            <input
              :type="showPassword ? 'text' : 'password'"
              class="reg-input w-100 p-2"
              v-model="newPassword"
              :disabled="loading"
              placeholder="••••••••"
              autocomplete="new-password"
            />
            <button type="button" class="password-toggle" @click="showPassword = !showPassword" tabindex="-1">
              <b-icon :icon="showPassword ? 'eye-slash' : 'eye'"></b-icon>
            </button>
          </div>
        </div>

        <div class="input-container mt-3">
          <label class="reg-label">Повторите пароль</label>
          <div class="password-wrapper">
            <input
              :type="showPasswordRe ? 'text' : 'password'"
              class="reg-input w-100 p-2"
              v-model="newPasswordRe"
              :disabled="loading"
              placeholder="••••••••"
              autocomplete="new-password"
              @keyup.enter="resetPassword"
            />
            <button type="button" class="password-toggle" @click="showPasswordRe = !showPasswordRe" tabindex="-1">
              <b-icon :icon="showPasswordRe ? 'eye-slash' : 'eye'"></b-icon>
            </button>
          </div>
        </div>

        <button
          class="submit-btn w-100 py-2 mt-4"
          :disabled="loading || !canReset"
          @click="resetPassword"
        >
          <b-spinner v-if="loading" small class="mr-2"></b-spinner>
          <span class="submit-btn-text">{{ loading ? 'Сохранение…' : 'Сохранить пароль' }}</span>
        </button>

        <div class="text-center mt-3">
          <a href="javascript:void(0)" class="fp-resend-link" @click="goBackToEmail">
            Отправить код повторно
          </a>
        </div>
      </div>

      <!-- Step 3: success -->
      <div v-else-if="step === 'success'" class="text-center mt-4">
        <div class="fp-success-icon-wrap">
          <b-icon icon="check-circle-fill" class="fp-success-icon"></b-icon>
        </div>
        <h6 class="fp-success-title mt-3">Пароль успешно изменён</h6>
        <p class="fp-desc">Теперь вы можете войти с новым паролем.</p>
        <button class="submit-btn w-100 py-2 mt-3" @click="$router.push('/login')">
          <span class="submit-btn-text">Войти в аккаунт</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '@/config/config'

export default {
  name: 'ForgotPassword',

  data() {
    return {
      step: 1,
      email: '',
      code: '',
      newPassword: '',
      newPasswordRe: '',
      showPassword: false,
      showPasswordRe: false,
      loading: false,
      error: '',
      turnstileToken: '',
      turnstileWidgetId: null,
    }
  },

  computed: {
    turnstileSiteKey() {
      return process.env.VUE_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAFe9KnHziunj8ygU'
    },
    currentLang() {
      return (this.$store.state.i18n && this.$store.state.i18n.locale) || 'ru'
    },
    canReset() {
      return (
        this.code.length === 4 &&
        this.newPassword.length >= 6 &&
        this.newPassword === this.newPasswordRe
      )
    },
  },

  mounted() {
    // Виджет Turnstile на шаге ввода email (шаг 1).
    this.$nextTick(() => this.renderTurnstile())
  },

  methods: {
    loadTurnstileScript() {
      return new Promise((resolve) => {
        if (window.turnstile) { resolve(); return }
        const existing = document.getElementById('cf-turnstile-script')
        if (existing) { existing.addEventListener('load', () => resolve()); return }
        const s = document.createElement('script')
        s.id = 'cf-turnstile-script'
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
        s.async = true; s.defer = true
        s.onload = () => resolve()
        document.head.appendChild(s)
      })
    },
    renderTurnstile() {
      this.loadTurnstileScript().then(() => {
        const el = this.$refs.turnstile
        if (!el || !window.turnstile) return
        if (this.turnstileWidgetId !== null) return
        this.turnstileWidgetId = window.turnstile.render(el, {
          sitekey: this.turnstileSiteKey,
          callback: (token) => { this.turnstileToken = token },
          'error-callback': () => { this.turnstileToken = '' },
          'expired-callback': () => { this.turnstileToken = '' },
        })
      })
    },
    resetTurnstile() {
      this.turnstileToken = ''
      if (window.turnstile && this.turnstileWidgetId !== null) {
        try { window.turnstile.reset(this.turnstileWidgetId) } catch (e) { /* noop */ }
      }
    },
    sendCode() {
      if (!this.email || this.loading) return
      this.error = ''
      this.loading = true
      axios
        .post(`${apiDomain}/user/open/forgotpassword`, {
          email: this.email,
          lang: this.currentLang,
        }, { headers: { 'Cf-Turnstile-Response': this.turnstileToken } })
        .then(res => {
          if (res.status === 200) {
            this.step = 2
          } else {
            this.error = 'Не удалось отправить код. Попробуйте позже.'
          }
        })
        .catch(err => {
          const body = err.response && err.response.data
          if (err.response && err.response.status === 403) {
            this.error = 'Проверка «я не робот» не пройдена. Обновите страницу и попробуйте снова.'
          } else if (err.response && err.response.status === 404 && body === 'no-user') {
            this.error = 'Пользователь с таким email не найден.'
          } else {
            this.error = 'Не удалось отправить код. Попробуйте позже.'
          }
          this.resetTurnstile()
        })
        .finally(() => {
          this.loading = false
        })
    },

    resetPassword() {
      if (!this.canReset || this.loading) return
      this.error = ''
      this.loading = true
      axios
        .post(`${apiDomain}/user/open/resetpassword`, {
          email: this.email,
          code: this.code,
          password: this.newPassword,
        })
        .then(res => {
          if (res.status === 200) {
            this.step = 'success'
          } else {
            this.error = 'Не удалось сохранить пароль. Попробуйте позже.'
          }
        })
        .catch(err => {
          const status = err.response && err.response.status
          const body = err.response && err.response.data
          if (status === 401 && body === 'wrong-code') {
            this.error = 'Неверный код. Проверьте письмо и попробуйте снова.'
          } else if (status === 404 && body === 'no-user') {
            this.error = 'Email не найден.'
          } else {
            this.error = 'Не удалось сохранить пароль. Попробуйте позже.'
          }
        })
        .finally(() => {
          this.loading = false
        })
    },

    goBackToEmail() {
      this.step = 1
      this.code = ''
      this.newPassword = ''
      this.newPasswordRe = ''
      this.error = ''
    },
  },
}
</script>

<style scoped>
.login-right {
  background: var(--Neutrals-1, #141416);
  min-height: 110vh;
}
.no-style {
  color: inherit;
  text-decoration: none;
}
.reg-form {
  display: block;
  width: 100%;
  max-width: 380px;
  margin: 0 auto;
}
.reg-title {
  color: var(--Neutrals-8, #FCFCFD);
  font-family: Inter;
  font-size: 40px;
  font-weight: 700;
  line-height: 48px;
  letter-spacing: -1.2px;
}
.reg-label {
  color: var(--Neutrals-5, #B1B5C3);
  font-family: Inter;
  font-size: 12px;
  font-weight: 700;
  line-height: 12px;
  text-transform: uppercase;
}
.reg-input {
  color: var(--Neutrals-4, #777E90);
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  background-color: transparent;
  border-radius: 12px;
  border: 2px solid var(--Neutrals-3, #353945);
  outline: none;
}
.reg-input:focus {
  border-color: var(--Primary-1, #EFB914);
}
.submit-btn {
  appearance: none;
  cursor: pointer;
  border-radius: 12px;
  border: 1px solid var(--Primary-1, #EFB914);
  background: var(--Primary-1, #EFB914);
}
.submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.submit-btn-text {
  color: var(--Neutrals-8, #FCFCFD);
  font-family: Inter;
  font-size: 16px;
  font-weight: 700;
  line-height: 16px;
}
.fp-desc {
  color: rgba(255, 255, 255, 0.6);
  font-family: Inter;
  font-size: 14px;
  line-height: 1.55;
}
.fp-email {
  color: var(--Neutrals-8, #FCFCFD);
}
.fp-error-box {
  background: rgba(220, 53, 69, 0.12);
  border: 1px solid rgba(220, 53, 69, 0.35);
  border-radius: 12px;
  padding: 12px 16px;
  color: #f87171;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
}
.fp-code-input {
  letter-spacing: 0.3em;
  font-size: 20px !important;
  font-weight: 700 !important;
  color: var(--Neutrals-8, #FCFCFD) !important;
}
.password-wrapper {
  position: relative;
}
.password-wrapper .reg-input {
  padding-right: 2.5rem !important;
}
.password-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--Neutrals-4, #777E90);
  display: flex;
  align-items: center;
}
.password-toggle:hover {
  color: var(--Neutrals-8, #FCFCFD);
}
.fp-resend-link {
  color: var(--Primary-1, #EFB914);
  font-family: Inter;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
}
.fp-resend-link:hover {
  text-decoration: underline;
}
.fp-success-icon-wrap {
  display: flex;
  justify-content: center;
}
.fp-success-icon {
  font-size: 3rem;
  color: #22c55e;
}
.fp-success-title {
  color: var(--Neutrals-8, #FCFCFD);
  font-family: Inter;
  font-size: 1.25rem;
  font-weight: 800;
}
</style>
